import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { BarChart3, TrendingDown, CheckCircle, AlertCircle, Music, DollarSign } from "lucide-react";
import { SHOW_COUNT_DATA, SHOW_COUNT_OVERVIEW, getShowCountInfo } from "@/lib/calculator-education";

interface ShowCountModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCount?: number;
  onCountSelect?: (count: number) => void;
}

export function ShowCountModal({ isOpen, onClose, selectedCount = 1, onCountSelect }: ShowCountModalProps) {
  const [activeRange, setActiveRange] = useState(getShowCountInfo(selectedCount).range);

  const ranges = ['1', '2-4', '5-9', '10-19', '20+'];
  const activeRangeInfo = SHOW_COUNT_DATA[activeRange];

  const getRangeColor = (range: string) => {
    switch (range) {
      case '1': return 'text-red-600 bg-red-50 border-red-200';
      case '2-4': return 'text-orange-600 bg-orange-50 border-orange-200';
      case '5-9': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case '10-19': return 'text-green-600 bg-green-50 border-green-200';
      case '20+': return 'text-purple-600 bg-purple-50 border-purple-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getRangeLabel = (range: string) => {
    switch (range) {
      case '1': return 'Highest Cost';
      case '2-4': return 'Moderate Cost';
      case '5-9': return 'Good Value';
      case '10-19': return 'Great Value';
      case '20+': return 'Best Value';
      default: return '';
    }
  };

  const handleRangeSelect = (range: string) => {
    setActiveRange(range);
    if (onCountSelect) {
      // Set count to middle of range for demo purposes
      const countMap: Record<string, number> = {
        '1': 1,
        '2-4': 3,
        '5-9': 7,
        '10-19': 15,
        '20+': 25
      };
      onCountSelect(countMap[range]);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Number of Shows & Economies of Scale</DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full pr-4">
            <div className="space-y-6">
              {/* Overview Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-max-primary" />
                    Why More Shows = Lower Cost Per Show
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    {SHOW_COUNT_OVERVIEW}
                  </p>
                  
                  {/* Cost Efficiency Visual */}
                  <div className="bg-gradient-to-r from-red-50 via-yellow-50 to-green-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Cost Efficiency Scale</span>
                      <TrendingDown className="w-4 h-4 text-green-600" />
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gradient-to-r from-red-200 via-yellow-200 to-green-200 h-2 rounded-full" />
                      <span className="text-xs text-muted-foreground">More Shows = Lower Cost/Show</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Range Selection */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                {ranges.map((range) => {
                  const isActive = range === activeRange;
                  const label = getRangeLabel(range);
                  
                  return (
                    <Button
                      key={range}
                      variant={isActive ? "default" : "outline"}
                      onClick={() => handleRangeSelect(range)}
                      className={`h-auto p-3 flex flex-col items-center gap-2 ${
                        isActive ? 'bg-max-primary hover:bg-max-primary/90' : ''
                      }`}
                    >
                      <Music className="w-4 h-4" />
                      <span className="text-xs font-medium text-center">
                        {SHOW_COUNT_DATA[range].range}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {label}
                      </Badge>
                    </Button>
                  );
                })}
              </div>

              {/* Active Range Details */}
              {activeRangeInfo && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <Badge className={getRangeColor(activeRange)}>
                        {activeRangeInfo.range}
                      </Badge>
                      <CardTitle>Sponsoring {activeRangeInfo.range}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <p className="text-muted-foreground leading-relaxed">
                      {activeRangeInfo.description}
                    </p>

                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Benefits */}
                      <div>
                        <h4 className="font-semibold mb-3 flex items-center gap-2 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          Benefits
                        </h4>
                        <ul className="space-y-2">
                          {activeRangeInfo.benefits.map((benefit, index) => (
                            <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                              <CheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0 text-green-500" />
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Considerations */}
                      <div>
                        <h4 className="font-semibold mb-3 flex items-center gap-2 text-orange-600">
                          <AlertCircle className="w-4 h-4" />
                          Considerations
                        </h4>
                        <ul className="space-y-2">
                          {activeRangeInfo.considerations.map((consideration, index) => (
                            <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                              <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0 text-orange-500" />
                              {consideration}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Cost Efficiency Indicator */}
                    <div className="bg-gradient-to-r from-blue-50 to-green-50 p-4 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-3">
                        <DollarSign className="w-5 h-5 text-blue-600" />
                        <div>
                          <h4 className="font-semibold text-blue-600">Cost Efficiency Level</h4>
                          <p className="text-sm text-muted-foreground">
                            {activeRange === '1' && "Highest cost per show - Single activation setup"}
                            {activeRange === '2-4' && "Moderate cost per show - Beginning economies of scale"}
                            {activeRange === '5-9' && "Good cost per show - Noticeable cost efficiencies"}
                            {activeRange === '10-19' && "Great cost per show - Significant savings and integration"}
                            {activeRange === '20+' && "Best cost per show - Maximum efficiency and partnership depth"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}